/**
 * Modal Custom Elements
 *
 * ModalDialog - Modal container with accessibility
 * ModalOpener - Button that opens modals
 * DeferredMedia - Lazy-load media (videos, 3D models)
 *
 * @example
 * <modal-opener data-modal="#MyModal">
 *   <button>Open Modal</button>
 * </modal-opener>
 *
 * <modal-dialog id="MyModal">
 *   <div role="dialog">
 *     <button id="ModalClose-MyModal">Close</button>
 *     <!-- modal content -->
 *   </div>
 * </modal-dialog>
 */
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      'click',
      this.hide.bind(this, false)
    );
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (
          event.pointerType === 'mouse' &&
          !event.target.closest('deferred-media, product-model')
        )
          this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    let isOpen = false;

    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();

    document.querySelectorAll('body > quick-add-modal').forEach((el) => {
      if (el.hasAttribute('open')) {
        isOpen = true;
      }
    });

    if (!isOpen) {
      document.body.classList.remove('overflow-hidden');
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
    }
  }
}

customElements.define('modal-dialog', ModalDialog);

/**
 * ModalOpener - Triggers modal opening
 */
class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modalId = this.getAttribute('data-modal');
      const modal = document.querySelector(modalId);

      // Try to open modal
      if (modal && typeof modal.show === 'function') {
        modal.show(button);
      } else if (modal) {
        // Fallback: wait one frame and try again (custom element might be initializing)
        requestAnimationFrame(() => {
          if (typeof modal.show === 'function') {
            modal.show(button);
          }
        });
      }
    });
  }
}

customElements.define('modal-opener', ModalOpener);

/**
 * DeferredMedia - Lazy-load videos and 3D models
 * Loads media only when poster is clicked or autoplay is enabled
 */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="Deferred-Poster-"]')?.addEventListener(
      'click',
      this.loadContent.bind(this)
    );
    if (this.getAttribute('data-autoplay')) {
      this.loadContent();
    }
  }

  loadContent() {
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(
        this.querySelector('template').content.firstElementChild.cloneNode(true)
      );
      this.setAttribute('loaded', true);
      window.pauseAllMedia();
      const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (this.getAttribute('data-autoplay')) {
              let playPromise = entry.target.play();
              if (playPromise !== undefined) {
                playPromise.then((_) => {}).catch((error) => {});
              }
            }
          } else {
            entry.target.pause();
          }
        });
      });
      const deferredElement = this.appendChild(
        content.querySelector('video, model-viewer, iframe')
      );

      if (
        deferredElement.nodeName == 'VIDEO' ||
        deferredElement.nodeName == 'IFRAME'
      ) {
        // force autoplay for safari

        if (this.classList.contains('video-section__media')) {
          let playPromise = deferredElement.play();
          if (playPromise !== undefined) {
            playPromise.then((_) => {}).catch((error) => {});
          }
          videoObserver.observe(deferredElement);
        } else {
          deferredElement.play();
        }
      }
      if (
        this.closest('.swiper')?.swiper.slides[
          this.closest('.swiper').swiper.activeIndex
        ].querySelector('model-viewer')
      ) {
        if (
          !this.closest('.swiper')
            .swiper.slides[this.closest('.swiper').swiper.activeIndex].querySelector(
              'model-viewer'
            )
            .classList.contains('shopify-model-viewer-ui__disabled')
        ) {
          this.closest('.swiper').swiper.params.noSwiping = true;
          this.closest('.swiper').swiper.params.noSwipingClass = 'swiper-slide';
        }
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);
